import { useMemo } from "react";
import {
  Button,
  Modal,
  LeaderboardFilterChips,
} from "@vms/ui-web";
import type { Leaderboard } from "@vms/types";
import { useOrgCars, useOrgEvents, useOrgTracks, type OrgEvent } from "../../api/orgContent";
import { useQuery } from "../../api/queries";
import { useOrgContext } from "../../context/OrgContext";

interface BrandingTemplateLite {
  id: string;
  name: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  leaderboard: Leaderboard;
  venueId: string | null;
  /** Optional display URL override (defaults to /display/leaderboard/<id>). */
  displayUrl?: string;
}

/**
 * Read-only detail view for a leaderboard. Resolves track / car / event IDs
 * to human labels using the org-content endpoints, scoped to the
 * leaderboard's venue (or `venueIds` for cloud-authored cross-venue rows).
 *
 * Hub-authored venue leaderboards are read-only on the cloud (sync authority
 * is hub-master) — the footer redirects the operator to the hub UI for
 * editing. Cloud-authored rows have their own edit form via OrgLeaderboardForm.
 */
export function LeaderboardDetailModal({
  isOpen,
  onClose,
  leaderboard,
  venueId,
  displayUrl,
}: Props): JSX.Element {
  const { currentOrg } = useOrgContext();

  // For the lookup scope: a hub-authored row has a `venueId`; a
  // cloud-authored row uses its `venueIds` (or undefined = all org venues).
  const scopeVenueIds = useMemo<string[] | null>(() => {
    if (venueId) return [venueId];
    if (leaderboard.venueIds && leaderboard.venueIds.length > 0) {
      return leaderboard.venueIds;
    }
    return null; // null = all org venues (only for cloud-authored "all")
  }, [venueId, leaderboard.venueIds]);

  const { data: tracks } = useOrgTracks(currentOrg?.id, leaderboard.game, scopeVenueIds);
  const { data: cars } = useOrgCars(currentOrg?.id, leaderboard.game, scopeVenueIds);
  const { data: events } = useOrgEvents(currentOrg?.id, scopeVenueIds);
  const branding = useQuery<BrandingTemplateLite[]>(
    currentOrg && leaderboard.brandingTemplateId
      ? `/api/branding-templates?organisationId=${currentOrg.id}`
      : null,
  );

  const brandingName = useMemo(() => {
    if (!leaderboard.brandingTemplateId) return null;
    return (
      branding.data?.find((b) => b.id === leaderboard.brandingTemplateId)?.name ?? null
    );
  }, [branding.data, leaderboard.brandingTemplateId]);

  const eventLookup = useMemo(() => {
    const map = new Map<string, OrgEvent>();
    for (const e of events ?? []) map.set(e.id, e);
    return map;
  }, [events]);

  const url = displayUrl ?? `/display/leaderboard/${leaderboard.id}`;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={leaderboard.name} size="lg">
      <div className="space-y-5 text-sm">
        {leaderboard.subheading && (
          <p className="text-gray-600 italic">{leaderboard.subheading}</p>
        )}

        <section>
          <div className="text-xs uppercase tracking-wider text-gray-500 mb-2">
            Settings
          </div>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <div>
              <dt className="text-gray-500">Game</dt>
              <dd className="text-gray-900">{leaderboard.game}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Branding</dt>
              <dd className="text-gray-900">{brandingName ?? "Default"}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Entry limit</dt>
              <dd className="text-gray-900">
                {leaderboard.entryLimit != null ? leaderboard.entryLimit : "Default (100)"}
              </dd>
            </div>
            <div>
              <dt className="text-gray-500">Date range</dt>
              <dd className="text-gray-900">
                {leaderboard.dateFrom || leaderboard.dateTo
                  ? `${leaderboard.dateFrom ?? "…"} – ${leaderboard.dateTo ?? "…"}`
                  : "All time"}
              </dd>
            </div>
          </dl>
        </section>

        <section>
          <div className="text-xs uppercase tracking-wider text-gray-500 mb-2">
            Active filters
          </div>
          <LeaderboardFilterChips
            game={leaderboard.game}
            eventIds={leaderboard.eventIds}
            tracks={leaderboard.tracks}
            cars={leaderboard.cars}
            dateFrom={leaderboard.dateFrom}
            dateTo={leaderboard.dateTo}
          />
        </section>

        {leaderboard.tracks.length > 0 && (
          <section>
            <div className="text-xs uppercase tracking-wider text-gray-500 mb-2">
              Tracks ({leaderboard.tracks.length})
            </div>
            <div className="space-y-2">
              {leaderboard.tracks.map((entry) => {
                const [trackId, layoutId] = entry.split("/");
                const track = tracks?.find((t) => t.trackId === trackId);
                const layout = layoutId
                  ? track?.layouts?.find((l) => l.layoutId === layoutId)
                  : null;
                const outlineUrl = layout?.outlineUrl ?? track?.outlineUrl;
                return (
                  <div
                    key={entry}
                    className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 bg-white"
                  >
                    {outlineUrl ? (
                      <div className="w-10 h-10 bg-gray-800 rounded flex-shrink-0 flex items-center justify-center p-0.5">
                        <img src={outlineUrl} alt="" className="max-w-full max-h-full object-contain" />
                      </div>
                    ) : track?.previewUrl ? (
                      <img src={track.previewUrl} alt="" className="w-10 h-10 object-cover rounded flex-shrink-0" />
                    ) : null}
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 truncate">
                        {track?.name ?? trackId}
                      </p>
                      {layout && (
                        <p className="text-xs text-gray-500 truncate">
                          {[
                            layout.name,
                            layout.year,
                            layout.type,
                            layout.lengthM ? `${(layout.lengthM / 1000).toFixed(2)} km` : null,
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {leaderboard.cars.length > 0 && (
          <section>
            <div className="text-xs uppercase tracking-wider text-gray-500 mb-2">
              Cars ({leaderboard.cars.length})
            </div>
            <div className="space-y-2">
              {leaderboard.cars.map((carId) => {
                const car = cars?.find((c) => c.carId === carId);
                const firstPreview = car?.skins?.find((s) => s.previewUrl)?.previewUrl;
                return (
                  <div
                    key={carId}
                    className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 bg-white"
                  >
                    {firstPreview ? (
                      <div className="w-12 h-9 bg-gray-900 rounded flex-shrink-0 flex items-center justify-center">
                        <img src={firstPreview} alt="" className="max-w-full max-h-full object-contain rounded" />
                      </div>
                    ) : car?.badgeUrl ? (
                      <img src={car.badgeUrl} alt="" className="w-9 h-9 object-contain flex-shrink-0" />
                    ) : null}
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 truncate">{car?.name ?? carId}</p>
                      {car?.brand && <p className="text-xs text-gray-500">{car.brand}</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {leaderboard.eventIds.length > 0 && (
          <section>
            <div className="text-xs uppercase tracking-wider text-gray-500 mb-2">
              Events ({leaderboard.eventIds.length})
            </div>
            <ul className="space-y-1.5">
              {leaderboard.eventIds.map((eventId) => {
                const ev = eventLookup.get(eventId);
                if (!ev) {
                  return (
                    <li key={eventId} className="text-sm text-gray-500">
                      <span className="font-mono">{eventId}</span>
                    </li>
                  );
                }
                const date = ev.startDate
                  ? new Date(ev.startDate).toLocaleDateString(undefined, { dateStyle: "medium" })
                  : null;
                return (
                  <li
                    key={eventId}
                    className="flex items-baseline gap-2 text-sm text-gray-700"
                  >
                    <span className="font-medium text-gray-900">{ev.name}</span>
                    {date && <span className="text-gray-500">· {date}</span>}
                    {ev.trackName && <span className="text-gray-500">· {ev.trackName}</span>}
                    {ev.venueName && <span className="text-gray-500">· {ev.venueName}</span>}
                  </li>
                );
              })}
            </ul>
          </section>
        )}
      </div>

      <div className="mt-6 pt-4 border-t border-gray-100 flex items-center justify-between">
        <p className="text-xs text-gray-500">
          {venueId
            ? "Editing happens on the hub UI for this venue."
            : "Cloud-authored cross-venue leaderboard."}
        </p>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={() => window.open(url, "_blank", "noopener")}
          >
            Open display
          </Button>
          <Button type="button" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
}
